import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Progress,
  FormCheckbox,
  FormTextarea,
  Alert
} from "shards-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBrain, 
  faCheckCircle, 
  faTimesCircle, 
  faPercent, 
  faSync,
  faInfoCircle,
  faSpinner,
  faThumbsUp,
  faCreditCard,
  faEye,
  faImages,
  faClock,
  faFileImage,
  faFile,
  faReceipt,
  faExternalLinkAlt
} from "@fortawesome/free-solid-svg-icons";

const DisputeAIAnalysis = ({ citation, dispute, onAnalyze, analysis, isAnalyzing, rules, onFeedback, onQuickView }) => {
  const [expanded, setExpanded] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [wasHelpful, setWasHelpful] = useState(true);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const getValidityColorClass = (percentage) => {
    if (percentage >= 75) return "success";
    if (percentage >= 40) return "warning";
    return "danger";
  };

  const getRecommendationIcon = (recommendation) => {
    switch (recommendation) {
      case "Accept":
        return <FontAwesomeIcon icon={faCheckCircle} className="text-success" />;
      case "Reject":
        return <FontAwesomeIcon icon={faTimesCircle} className="text-danger" />;
      case "Adjust":
        return <FontAwesomeIcon icon={faPercent} className="text-warning" />;
      default:
        return <FontAwesomeIcon icon={faInfoCircle} className="text-muted" />;
    }
  };

  const handleAnalyzeClick = () => {
    onAnalyze(citation, dispute, rules);
  };

  const handleFeedbackSubmit = () => {
    if (onFeedback) {
      onFeedback({
        disputeId: dispute.id,
        citationId: citation.id,
        wasHelpful,
        feedbackText,
        recommendation: analysis.recommendation,
        analysisId: analysis.id || Date.now().toString()
      });
      setFeedbackSubmitted(true);
    }
  };

  const handleQuickViewClick = (e) => {
    e.preventDefault();
    if (onQuickView) {
      onQuickView(citation);
    }
  };
  
  const openFullCitation = (e) => {
    e.preventDefault();
    if (citation && citation.violation_number) {
      window.open(`/citation/${citation.violation_number}`, '_blank');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      return new Date(timestamp).toLocaleString();
    } catch (e) {
      return timestamp;
    }
  };

  return (
    <Card className="mb-3" style={{ borderRadius: "8px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
      <CardHeader className="d-flex align-items-center border-bottom">
        <FontAwesomeIcon icon={faBrain} className="mr-2" style={{ color: "#6772e5" }} />
        <h6 className="m-0 font-weight-bold">AI Dispute Analysis</h6>
        <div className="ml-auto">
          {analysis && (
            <Button 
              size="sm" 
              theme="light" 
              className="ml-2" 
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "Collapse" : "Expand"}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardBody style={{ maxHeight: "none", overflowY: "visible" }}>
        {isAnalyzing ? (
          <div className="text-center py-4">
            <FontAwesomeIcon icon={faSpinner} size="lg" className="text-primary" spin />
            <p className="mt-3 mb-0 text-muted">Analyzing dispute...</p>
          </div>
        ) : analysis ? (
          <div>
            {/* Citation link at the top */}
            <div className="mb-3">
              <Button block theme="light" onClick={openFullCitation} className="text-left" style={{ display: "flex", alignItems: "center" }}>
                <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" />
                <span>Open Full Citation #{citation.violation_number}</span>
              </Button>
            </div>
            
            {/* Display payment verification alert if found */}
            {analysis.validPaymentFound && (
              <Alert theme="success" className="mb-3">
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faCreditCard} className="mr-3" size="lg" />
                  <div>
                    <h5 className="mb-1">Valid Payment Verified</h5>
                    <p className="mb-0">A valid payment/reservation for this vehicle was found during citation time.</p>
                    {expanded && analysis.paymentDetails && (
                      <div className="mt-2 small">
                        <div><strong>Created:</strong> {formatTimestamp(analysis.paymentDetails.created_at)}</div>
                        <div><strong>Expires:</strong> {formatTimestamp(analysis.paymentDetails.expiry_time)}</div>
                      </div>
                    )}
                  </div>
                </div>
              </Alert>
            )}
            
            {/* Display photo evidence alert if considered */}
            {citation && citation.images && citation.images.length > 0 && analysis.consideredPhotoEvidence && (
              <Alert theme="warning" className="mb-3">
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faImages} className="mr-3" size="lg" />
                  <div>
                    <h5 className="mb-1">Photo Evidence Analyzed</h5>
                    <p className="mb-0">
                      {citation.images.length === 1 
                        ? "1 timestamped photo taken at the time of citation was analyzed." 
                        : `${citation.images.length} timestamped photos taken at the time of citation were analyzed.`}
                    </p>
                    {expanded && (
                      <div className="mt-2 small">
                        {citation.images.map((img, idx) => (
                          <div key={idx} className="mb-1">
                            <strong>Photo #{idx + 1}:</strong> Taken at <FontAwesomeIcon icon={faClock} className="ml-1 mr-1" size="xs" /> 
                            {img.created_at ? new Date(img.created_at).toLocaleString() : "Unknown time"}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Alert>
            )}
            
            {/* Display dispute evidence alert if considered */}
            {dispute && dispute.evidence && dispute.evidence.length > 0 && analysis.consideredDisputeEvidence && (
              <Alert theme="info" className="mb-3">
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faReceipt} className="mr-3" size="lg" />
                  <div>
                    <h5 className="mb-1">Disputant Evidence Analyzed</h5>
                    <p className="mb-0">
                      {dispute.evidence.length === 1 
                        ? "1 evidence file provided by the disputant was analyzed." 
                        : `${dispute.evidence.length} evidence files provided by the disputant were analyzed.`}
                    </p>
                    {expanded && (
                      <div className="mt-2">
                        <div className="small mb-2">
                          {dispute.evidence.map((evidence, idx) => (
                            <div key={idx} className="mb-1">
                              <strong>Evidence #{idx + 1}:</strong> {evidence.name || 'Unnamed file'} <FontAwesomeIcon icon={faFile} className="ml-1 mr-1" size="xs" /> 
                            </div>
                          ))}
                        </div>
                        
                        {analysis.disputeEvidenceAnalysis && (
                          <div className="mt-2 p-2" style={{ backgroundColor: 'rgba(33, 150, 243, 0.05)', borderRadius: '4px', border: '1px solid rgba(33, 150, 243, 0.1)' }}>
                            <strong>Analysis:</strong> {analysis.disputeEvidenceAnalysis}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Alert>
            )}
            
            <div className="d-flex align-items-center mb-3">
              <div className="mr-3" style={{ width: "120px" }}>
                <h6 className="mb-1 text-muted" style={{ fontSize: "12px" }}>Validity Score</h6>
                <h4 className={`text-${getValidityColorClass(analysis.validityPercentage)} mb-0`}>
                  {analysis.validityPercentage}%
                </h4>
              </div>
              <div style={{ flex: 1 }}>
                <Progress
                  value={analysis.validityPercentage}
                  theme={getValidityColorClass(analysis.validityPercentage)}
                  style={{ height: "8px" }}
                />
              </div>
            </div>

            <div className="mb-3">
              <h6 className="mb-1 text-muted" style={{ fontSize: "12px" }}>Recommended Action</h6>
              <div className="d-flex align-items-center">
                {getRecommendationIcon(analysis.recommendation)}
                <span className="ml-2 font-weight-bold">{analysis.recommendation}</span>
                {analysis.recommendation === "Adjust" && (
                  <span className="ml-2">({analysis.adjustmentPercentage}% reduction)</span>
                )}
              </div>
            </div>

            {expanded && (
              <div className="expanded-content" style={{ maxHeight: "350px", overflowY: "auto", padding: "10px", marginBottom: "15px", border: "1px solid rgba(0,0,0,0.05)", borderRadius: "4px" }}>
                <div className="mb-3">
                  <h6 className="mb-1 text-muted" style={{ fontSize: "12px" }}>Citation Details</h6>
                  <div className="p-3" style={{ backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: '4px', border: '1px solid rgba(0, 0, 0, 0.05)' }}>
                    <div className="mb-2">
                      <strong>Violation:</strong> {
                        analysis.violationDetails?.violations && analysis.violationDetails.violations.length > 0 
                          ? analysis.violationDetails.violations.map((violation, index) => (
                              <div key={index} className={index > 0 ? "mt-1" : ""}>
                                {violation.name}: ${violation.amount}
                              </div>
                            ))
                          : analysis.violationDetails?.type || citation.violation_type || "Unknown"
                      }
                    </div>
                    <div className="mb-2">
                      <strong>Date:</strong> {analysis.violationDetails?.date || 
                        (citation.created_at ? new Date(citation.created_at).toLocaleDateString() : 
                        citation.violation_on ? new Date(citation.violation_on).toLocaleDateString() : "Unknown")}
                    </div>
                    <div className="mb-2">
                      <strong>Time:</strong> {analysis.violationDetails?.time || 
                        (citation.created_at ? new Date(citation.created_at).toLocaleTimeString() : 
                        citation.violation_on ? new Date(citation.violation_on).toLocaleTimeString() : "Unknown")}
                    </div>
                    
                    {analysis.violationDetails?.involvedTimeRestriction && (
                      <div className="mt-3 p-2" style={{ backgroundColor: 'rgba(249, 164, 63, 0.1)', borderRadius: '4px', border: '1px solid rgba(249, 164, 63, 0.2)' }}>
                        <strong>Time Restriction:</strong> {analysis.violationDetails.restrictionDetails}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="mb-3">
                  <h6 className="mb-1 text-muted" style={{ fontSize: "12px" }}>AI Reasoning</h6>
                  <p className="mb-0" style={{ fontSize: "14px" }}>{analysis.reasoning}</p>
                </div>
                
                {analysis.recommendation === "Adjust" && analysis.adjustmentReason && (
                  <div className="mb-0">
                    <h6 className="mb-1 text-muted" style={{ fontSize: "12px" }}>Adjustment Reason</h6>
                    <p className="mb-0" style={{ fontSize: "14px" }}>{analysis.adjustmentReason}</p>
                  </div>
                )}
              </div>
            )}
            
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                <Button 
                  size="sm" 
                  theme="light" 
                  onClick={() => setShowFeedback(!showFeedback)} 
                  disabled={feedbackSubmitted}
                  className="mr-2"
                >
                  <FontAwesomeIcon icon={faThumbsUp} className="mr-1" />
                  {feedbackSubmitted ? "Feedback Submitted" : "Provide Feedback"}
                </Button>
                
                {onQuickView && (
                  <Button 
                    size="sm" 
                    theme="light" 
                    onClick={handleQuickViewClick}
                  >
                    <FontAwesomeIcon icon={faEye} className="mr-1" />
                    View Dispute Details
                  </Button>
                )}
              </div>
              
              <Button size="sm" theme="light" onClick={handleAnalyzeClick}>
                <FontAwesomeIcon icon={faSync} className="mr-1" />
                Re-analyze
              </Button>
            </div>
            
            {showFeedback && !feedbackSubmitted && (
              <div className="mt-3 p-3 bg-light rounded">
                <h6 className="mb-2">Was this analysis helpful?</h6>
                <FormCheckbox
                  checked={wasHelpful}
                  onChange={() => setWasHelpful(!wasHelpful)}
                  className="mb-2"
                >
                  This recommendation was helpful
                </FormCheckbox>
                
                <FormTextarea
                  placeholder="Optional feedback to improve future analyses..."
                  value={feedbackText}
                  onChange={e => setFeedbackText(e.target.value)}
                  style={{ marginBottom: '15px', fontSize: '14px' }}
                />
                
                <div className="d-flex justify-content-end">
                  <Button size="sm" theme="light" onClick={() => setShowFeedback(false)} className="mr-2">
                    Cancel
                  </Button>
                  <Button size="sm" theme="primary" onClick={handleFeedbackSubmit}>
                    Submit Feedback
                  </Button>
                </div>
              </div>
            )}
            
            {feedbackSubmitted && showFeedback && (
              <div className="mt-3 p-3 bg-light rounded text-center text-success">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                Thank you for your feedback! This helps improve future analyses.
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-3">
            <p className="mb-3">Use AI to analyze this dispute for validity and get a recommendation.</p>
            <Button theme="primary" onClick={handleAnalyzeClick}>
              <FontAwesomeIcon icon={faBrain} className="mr-2" />
              Analyze Dispute
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default DisputeAIAnalysis; 